/**
 * The expectation editor's model.
 *
 * A reviewer's job on each draft is to answer "what should have happened?" and
 * the form exists to make that fast. The rule underneath it, from the rubric:
 * **a case only asserts what it states, and unstated fields are not scored.**
 *
 * So this converts form state to an expectation by OMITTING anything left
 * blank, rather than writing defaults. An expectation full of zeroes and empty
 * arrays would assert a dozen things nobody decided, and every one of those is
 * a case that fails for a reason its author never intended — which is how a
 * suite becomes something people learn to ignore.
 *
 * ## The round-trip must not eat what it does not edit
 *
 * The first version built the expectation from the form's own fields and
 * nothing else — and the import had already written `handlingCriteria` (the
 * auditor's sentence, the judged half of the case) and `passThreshold` into
 * these expectations. Every save through the form silently erased them: the
 * reviewer ticked two boxes, activated, and the case lost the very criterion
 * that made it worth importing. Worse, the "asserts nothing" warning showed on
 * cases that asserted plenty, because the form could not see it. Now the
 * criterion is a first-class field, and every key the form does not own rides
 * through `carry` untouched.
 */

export interface ExpectationForm {
  /** '' = not asserted, 'yes' / 'no' = asserted. */
  escalate: '' | 'yes' | 'no';
  /** '' = not asserted, 'none' = assert no offer, or a percentage. */
  offerPercent: string;
  requiredTools: string[];
  forbiddenActions: string[];
  expectedTags: string;
  forbiddenPhrases: string;
  mustNotMoveMoney: boolean;
  expectPendingApproval: '' | 'yes' | 'no';
  /**
   * '' = not asserted, 'yes' = a save should have been attempted, 'no' = none
   * was appropriate.
   *
   * The most important assertion on a cancellation and the one the form was
   * missing. The vendor's loudest complaint was being asked EIGHT TIMES before
   * a cancellation was honoured, and the whole save ladder exists to bound
   * that. `retention_conduct` grades it and had no way to be set from here.
   */
  expectSaveAttempt: '' | 'yes' | 'no';
  /** Blank uses the rubric default of one. The cap on how often we may ask. */
  maxSaveAttempts: string;
  toneGuidance: string;
  /**
   * The judged criterion — the auditor's sentence(s) about what the reply
   * should have done, one demand per line. On import this is the "what the
   * reply should have done differently" column; here it is editable, because
   * some notes need trimming to be about the REPLY and some cases arrive with
   * no note at all and a reviewer wants to write one.
   */
  criteria: string;
  /** Every expectation key the form does not own, carried through unchanged. */
  carry: Expectation;
}

export const EMPTY_FORM: ExpectationForm = {
  escalate: '',
  offerPercent: '',
  requiredTools: [],
  forbiddenActions: [],
  expectedTags: '',
  forbiddenPhrases: '',
  mustNotMoveMoney: false,
  expectPendingApproval: '',
  expectSaveAttempt: '',
  maxSaveAttempts: '',
  toneGuidance: '',
  criteria: '',
  carry: {},
};

export type Expectation = Record<string, unknown>;

/** Tools a reviewer can require, matching the agent's read surface. */
export const REVIEWABLE_TOOLS = [
  'getCustomerSnapshot',
  'getOrdersByEmail',
  'getSubscriptionsByEmail',
] as const;

/** Effects a reviewer can forbid. */
export const FORBIDDABLE_ACTIONS = [
  'refund_applied',
  'retention_refund_applied',
  'subscription_cancelled',
  'order_cancelled',
  'subscription_frequency_changed',
  'subscription_quantity_changed',
  'subscription_months_skipped',
  'subscription_reactivated',
] as const;

function splitList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Keys the form edits; everything else belongs to `carry`. */
const OWNED_KEYS = new Set([
  'escalate',
  'offerPercent',
  'requiredTools',
  'forbiddenActions',
  'expectedTags',
  'forbiddenPhrases',
  'mustNotMoveMoney',
  'expectPendingApproval',
  'expectSaveAttempt',
  'maxSaveAttempts',
  'toneGuidance',
  'handlingCriteria',
]);

export function toExpectation(form: ExpectationForm): Expectation {
  // Unowned keys first (passThreshold among them), so nothing the import or a
  // future field wrote is lost to a reviewer ticking one box.
  const expectation: Expectation = { ...form.carry };

  const criteria = form.criteria
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (criteria.length > 0) {
    expectation.handlingCriteria = criteria;
    // The import's threshold for criterion-bearing cases. Only written when
    // none was carried, so a deliberate override survives the round-trip.
    if (expectation.passThreshold === undefined) expectation.passThreshold = 0.9;
  }

  if (form.escalate !== '') expectation.escalate = form.escalate === 'yes';

  const percent = form.offerPercent.trim();
  if (percent === 'none') {
    // An explicit "no offer" is an assertion, and a valuable one — it is how a
    // case pins down that money should NOT have been put on the table.
    expectation.offerPercent = null;
  } else if (percent !== '') {
    const parsed = Number(percent);
    if (Number.isFinite(parsed)) expectation.offerPercent = parsed;
  }

  if (form.requiredTools.length > 0) expectation.requiredTools = [...form.requiredTools];
  if (form.forbiddenActions.length > 0) expectation.forbiddenActions = [...form.forbiddenActions];

  const tags = splitList(form.expectedTags);
  if (tags.length > 0) expectation.expectedTags = tags;

  const phrases = splitList(form.forbiddenPhrases);
  if (phrases.length > 0) expectation.forbiddenPhrases = phrases;

  if (form.mustNotMoveMoney) expectation.mustNotMoveMoney = true;
  if (form.expectPendingApproval !== '') {
    expectation.expectPendingApproval = form.expectPendingApproval === 'yes';
  }

  if (form.expectSaveAttempt !== '') {
    expectation.expectSaveAttempt = form.expectSaveAttempt === 'yes';
  }
  const cap = form.maxSaveAttempts.trim();
  if (cap !== '') {
    const parsed = Number(cap);
    if (Number.isInteger(parsed)) expectation.maxSaveAttempts = parsed;
  }

  const tone = form.toneGuidance.trim();
  if (tone) expectation.toneGuidance = tone;

  return expectation;
}

export function fromExpectation(expectation: Expectation | null | undefined): ExpectationForm {
  if (!expectation || typeof expectation !== 'object') return { ...EMPTY_FORM, carry: {} };
  const e = expectation;

  const percent =
    e.offerPercent === null ? 'none' : typeof e.offerPercent === 'number' ? String(e.offerPercent) : '';

  const carry: Expectation = {};
  for (const [key, value] of Object.entries(e)) {
    if (!OWNED_KEYS.has(key)) carry[key] = value;
  }

  return {
    criteria: Array.isArray(e.handlingCriteria)
      ? (e.handlingCriteria as unknown[]).filter((c) => typeof c === 'string').join('\n')
      : '',
    carry,
    escalate: typeof e.escalate === 'boolean' ? (e.escalate ? 'yes' : 'no') : '',
    offerPercent: percent,
    requiredTools: Array.isArray(e.requiredTools) ? (e.requiredTools as string[]) : [],
    forbiddenActions: Array.isArray(e.forbiddenActions) ? (e.forbiddenActions as string[]) : [],
    expectedTags: Array.isArray(e.expectedTags) ? (e.expectedTags as string[]).join(', ') : '',
    forbiddenPhrases: Array.isArray(e.forbiddenPhrases)
      ? (e.forbiddenPhrases as string[]).join(', ')
      : '',
    mustNotMoveMoney: e.mustNotMoveMoney === true,
    expectPendingApproval:
      typeof e.expectPendingApproval === 'boolean' ? (e.expectPendingApproval ? 'yes' : 'no') : '',
    expectSaveAttempt:
      typeof e.expectSaveAttempt === 'boolean' ? (e.expectSaveAttempt ? 'yes' : 'no') : '',
    maxSaveAttempts: typeof e.maxSaveAttempts === 'number' ? String(e.maxSaveAttempts) : '',
    toneGuidance: typeof e.toneGuidance === 'string' ? e.toneGuidance : '',
  };
}

export function isEmptyExpectation(expectation: Expectation | null | undefined): boolean {
  if (!expectation || typeof expectation !== 'object') return true;
  return Object.keys(expectation).length === 0;
}

/**
 * Problems that should stop a case being activated.
 *
 * Distinct from "it is empty": these are assertions that contradict each other,
 * and a self-contradicting case fails forever with nobody able to say why.
 */
export function validateExpectation(form: ExpectationForm): string[] {
  const problems: string[] = [];
  const expectation = toExpectation(form);

  if (isEmptyExpectation(expectation)) {
    problems.push('This case asserts nothing, so it would pass whatever the agent did.');
  }

  const percent = form.offerPercent.trim();
  if (percent !== '' && percent !== 'none') {
    const parsed = Number(percent);
    if (!Number.isFinite(parsed)) problems.push('The offer percentage must be a number, or "none".');
    else if (parsed < 0 || parsed > 100) problems.push('The offer percentage must be between 0 and 100.');
    else if (!Number.isInteger(parsed)) problems.push('The offer percentage must be a whole number.');
  }

  // Asserting an offer while forbidding money from moving cannot both hold.
  if (form.mustNotMoveMoney && percent !== '' && percent !== 'none' && Number(percent) > 0) {
    problems.push(
      'This says no money may move AND that a refund should be offered. Pick one — an offer that is never paid is not a refund.',
    );
  }

  if (form.escalate === 'yes' && percent !== '' && percent !== 'none' && Number(percent) > 0) {
    problems.push(
      'This says a human should take the ticket AND that the agent should have offered a refund. Decide which is the right answer.',
    );
  }

  const cap = form.maxSaveAttempts.trim();
  if (cap !== '') {
    const parsed = Number(cap);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 3) {
      problems.push('The save-attempt cap must be a whole number from 0 to 3.');
    }
  }

  // "Never offer a save" and "you may offer up to two" cannot both be the
  // right answer, and a case saying both fails forever with nobody able to
  // explain why.
  if (form.expectSaveAttempt === 'no' && cap !== '' && Number(cap) > 0) {
    problems.push(
      'This says no save was appropriate AND allows save attempts. Set the cap to 0, or say a save was expected.',
    );
  }

  if (form.expectSaveAttempt === 'yes' && cap === '0') {
    problems.push('This expects a save attempt and caps attempts at zero.');
  }

  return problems;
}

/** A one-line summary for the queue list. */
export function describeExpectation(expectation: Expectation | null | undefined): string {
  if (isEmptyExpectation(expectation)) return 'nothing asserted yet';
  const e = expectation as Expectation;
  const parts: string[] = [];

  if (Array.isArray(e.handlingCriteria) && e.handlingCriteria.length > 0) {
    parts.push('auditor criterion');
  }
  if (e.escalate === true) parts.push('escalate');
  if (e.escalate === false) parts.push('handle it');
  if (e.offerPercent === null) parts.push('no offer');
  else if (typeof e.offerPercent === 'number') parts.push(`${e.offerPercent}% offer`);
  if (e.mustNotMoveMoney === true) parts.push('no money moves');
  if (e.expectPendingApproval === true) parts.push('held for approval');
  if (e.expectSaveAttempt === true) parts.push('save attempted');
  if (e.expectSaveAttempt === false) parts.push('no save');
  if (Array.isArray(e.requiredTools) && e.requiredTools.length > 0) {
    parts.push(`${e.requiredTools.length} tool${e.requiredTools.length === 1 ? '' : 's'}`);
  }
  if (Array.isArray(e.forbiddenPhrases) && e.forbiddenPhrases.length > 0) {
    parts.push(`${e.forbiddenPhrases.length} forbidden phrase${e.forbiddenPhrases.length === 1 ? '' : 's'}`);
  }

  return parts.length > 0 ? parts.join(' · ') : 'asserted';
}
